import { Link } from 'react-router-dom'
import { Icon } from '../../components/icons/Icon'
import { StatusPill, type StatusPillVariant } from '../../components/StatusPill'
import { ForwardLookingCell } from '../../design-system/list-view/cells/ForwardLookingCell'
import type { ColumnDef } from '../../design-system/list-view/types'
import { label } from '../../lib/labels'
import type { CaseListView } from '../../types/case'
import { CaseProgressIndicator } from './CaseProgressIndicator'
import { deriveCaseProgressFromStatus } from './caseProgress'

const KIND_VARIANT: Record<string, StatusPillVariant> = { rnd: 'info', film: 'neutral' }

const CASE_TITLE_GAP = 'Case has no title field yet (data-model gap) — see docs/design/IB-53-film-ui-design.md.'
const FY_GAP =
  'No FiscalYear get-by-id read path yet (docs/design/IB-53-film-ui-design.md §7 Q2) — flagged to Prabhu/Tech Lead.'

/**
 * CLAUDE.md §4 — every cross-case list carries Account · Legal entity ·
 * Case · Fiscal year, in that order, for sort/filter/refine. Names, never
 * UUIDs — `CaseListView` (IB-53 backend projection) resolves
 * accountName/legalEntityName/caseTitle server-side.
 */
export function buildCaseColumns(): ColumnDef<CaseListView>[] {
  return [
    {
      id: 'accountName',
      header: 'Account',
      width: 200,
      minWidth: 160,
      sortable: false,
      pinnable: true,
      toExportText: (row) => row.accountName ?? '',
      render: (row) => row.accountName ?? <span style={{ color: 'var(--iw-text-subtle)' }}>—</span>,
    },
    {
      id: 'legalEntityName',
      header: 'Legal entity',
      width: 200,
      sortable: false,
      toExportText: (row) => row.legalEntityName ?? '',
      render: (row) => row.legalEntityName ?? <span style={{ color: 'var(--iw-text-subtle)' }}>—</span>,
    },
    {
      id: 'caseTitle',
      header: 'Case',
      width: 220,
      sortable: false,
      headerInfo: { label: 'Case column info', content: CASE_TITLE_GAP },
      toExportText: (row) => row.caseTitle ?? '',
      render: (row) => (
        <Link
          to={`/cases/${row.id}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--iw-accent)', textDecoration: 'none' }}
        >
          <Icon name={row.kind === 'film' ? 'film' : 'briefcase'} size={14} />
          {row.caseTitle ?? <ForwardLookingCell reason={CASE_TITLE_GAP} />}
        </Link>
      ),
    },
    {
      id: 'fiscalYearLabel',
      header: 'Fiscal year',
      width: 130,
      sortable: false,
      headerInfo: { label: 'Fiscal year column info', content: FY_GAP },
      toExportText: (row) => row.fiscalYearLabel ?? '',
      render: (row) => (row.fiscalYearId ? row.fiscalYearLabel ?? <ForwardLookingCell reason={FY_GAP} /> : <ForwardLookingCell reason={FY_GAP} />),
    },
    {
      id: 'kind',
      header: 'Type',
      width: 130,
      // Not backend-sortable (CaseService.RSQL only allow-lists overallStatus/createdAt/id) — the
      // R&D/Film toggle above is the real way to narrow by kind; this column stays display-only.
      sortable: false,
      toExportText: (row) => label('caseKind', row.kind),
      render: (row) => <StatusPill variant={KIND_VARIANT[row.kind] ?? 'neutral'} label={label('caseKind', row.kind)} />,
    },
    {
      id: 'overallStatus',
      header: 'Status',
      width: 120,
      sortable: true,
      toExportText: (row) => row.overallStatus,
      render: (row) => (
        <StatusPill variant={row.overallStatus === 'closed' ? 'muted' : 'success'} label={row.overallStatus === 'closed' ? 'Closed' : 'Open'} />
      ),
    },
    {
      id: 'progress',
      header: 'Progress',
      width: 150,
      minWidth: 130,
      // Client-derived only (CaseListView.overallStatus) — CaseService.RSQL has no matching sort
      // key for a derived column, so this stays display-only, same posture as the `kind` column
      // above.
      sortable: false,
      headerInfo: {
        label: 'Progress column info',
        content:
          'A coarse, at-a-glance read of where this Case sits (Open vs. Closed). The Case Overview ' +
          'tab shows the full stage-by-stage lifecycle for film cases.',
      },
      toExportText: (row) => {
        const p = deriveCaseProgressFromStatus(row.overallStatus)
        return `${p.percentComplete}% (${p.currentStageLabel})`
      },
      render: (row) => <CaseProgressIndicator progress={deriveCaseProgressFromStatus(row.overallStatus)} compact />,
    },
    {
      id: 'openedAt',
      header: 'Opened',
      width: 120,
      sortable: false,
      defaultHidden: true,
      toExportText: (row) => row.openedAt,
      render: (row) => new Date(row.openedAt).toLocaleDateString(),
    },
  ]
}

export const CASE_COLUMN_ORDER = [
  'accountName',
  'legalEntityName',
  'caseTitle',
  'fiscalYearLabel',
  'kind',
  'overallStatus',
  'progress',
  'openedAt',
]
